/**
 * Senior auto-advance ↔ listview bridge.
 *
 * The senior list view (qa-list.tsx) holds its filter + sort state in React
 * state that is lost when the senior navigates into a delivery detail page —
 * which is exactly where decisions (and thus auto-advance) happen. To let
 * auto-advance honor the listview's current filter + sort, the list mirrors its
 * state here, and the detail page reads it back and ships it to the server as
 * the decision body's `listContext`.
 *
 * Date is intentionally NOT carried: auto-advance is always scoped to today
 * (locked 2026-06-17), so a past-date list never auto-advances.
 */

export const SENIOR_LIST_CONTEXT_KEY = "qaSeniorListContext";

export interface SeniorListContext {
  /**
   * Sort key: delivery-completion time (`completed_desc` newest-first list default /
   * `completed_asc`) or QA-decision time (`qa_completed_desc` / `qa_completed_asc`,
   * the instant the agent submitted their QA decision).
   */
  sort: "completed_desc" | "completed_asc" | "qa_completed_desc" | "qa_completed_asc";
  q?: string;
  agentStatus: string[];
  owner: string[];
  driver: string[];
  attention: string[];
  /** E→A match buckets: "mismatch" | "all-match" | "undetected". */
  match: string[];
  /** YB / No YB buckets: "yb" | "not_yb" (SPEC-BagCollectionYB). */
  bag: string[];
}

export function writeSeniorListContext(ctx: SeniorListContext): void {
  try {
    localStorage.setItem(SENIOR_LIST_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    /* localStorage unavailable (private mode / quota) — auto-advance falls back
       to the server defaults (today's Needs-QA, newest-first, no filters). */
  }
}

export function readSeniorListContext(): SeniorListContext | null {
  try {
    const raw = localStorage.getItem(SENIOR_LIST_CONTEXT_KEY);
    return raw ? (JSON.parse(raw) as SeniorListContext) : null;
  } catch {
    return null;
  }
}

/**
 * Serialize the stored list context into the query-param shape /api/qa/queue/next
 * expects (sort, q, agentStatus[], owner[], driver[], attention[], match[], bag[]). Empty string
 * when no context is stored — the server then falls back to its defaults.
 */
export function seniorListContextQuery(): string {
  const ctx = readSeniorListContext();
  if (!ctx) return "";
  const p = new URLSearchParams();
  if (ctx.sort) p.set("sort", ctx.sort);
  if (ctx.q) p.set("q", ctx.q);
  ctx.agentStatus?.forEach((v) => p.append("agentStatus[]", v));
  ctx.owner?.forEach((v) => p.append("owner[]", v));
  ctx.driver?.forEach((v) => p.append("driver[]", v));
  ctx.attention?.forEach((v) => p.append("attention[]", v));
  ctx.match?.forEach((v) => p.append("match[]", v));
  ctx.bag?.forEach((v) => p.append("bag[]", v));
  return p.toString();
}
